/* Optional visual game. The timer never gates purchases or changes eligibility.
   Bunny delegates to its existing handler; Karol uses the shared guarded cart flow. */
(() => {
  'use strict';
  if (customElements.get('xdrop-gift-game')) return;
  const DURATION = 15 * 60 * 1000;
  const COPY = {
    en: {
      catch: 'Catch the mystery box', caught: 'Caught!', tap: 'TAP THE BOX',
      remaining: 'Visual round time remaining', success: 'Nice catch! Your visual round has started.',
      expired: 'Round finished. You can play again or keep shopping.',
      add: 'GET THE BOX', adding: 'Adding…', added: 'Added to your cart',
      unavailable: 'Not available right now', soon: 'COMING SOON',
      fallback: 'Use the free-box button above to continue.',
      error: 'Could not confirm the cart update. Check your cart before trying again.'
    },
    es: {
      catch: 'Atrapar la caja misteriosa', caught: '¡Atrapada!', tap: 'TOCA LA CAJA',
      remaining: 'Tiempo restante de la ronda visual', success: '¡Buena captura! Tu ronda visual ha empezado.',
      expired: 'Ronda terminada. Puedes jugar de nuevo o seguir comprando.',
      add: 'OBTENER CAJA', adding: 'Añadiendo…', added: 'Añadida a tu carrito',
      unavailable: 'No disponible ahora', soon: 'PRÓXIMAMENTE',
      fallback: 'Usa el botón de la caja gratuita de arriba para continuar.',
      error: 'No pudimos confirmar el cambio en el carrito. Revísalo antes de reintentar.'
    }
  };
  class GiftGame extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.q = selector => this.querySelector(selector);
      this.artist = this.dataset.artist === 'karol' ? 'karol' : 'bunny';
      this.host = this.closest('x-drop-halloween') || this.closest('.kg-shell');
      this.controller = new AbortController();
      this.pending = false;
      this.completed = false;
      this.forwarding = false;
      this.caught = false;
      this.end = 0;
      this.message = '';
      this.messageKey = '';
      this.lang = '';
      this.readRound();
      const options = {signal: this.controller.signal};
      this.dialog = this.q('dialog');
      this.q('[data-xgg-close]').addEventListener('click', () => this.closeGame(), options);
      this.dialog.addEventListener('close', () => { const target = this.opener?.isConnected && !this.opener.disabled ? this.opener : this.host?.querySelector('h1,h2'); if(target){ if(!target.hasAttribute('tabindex') && target.matches('h1,h2')) target.setAttribute('tabindex','-1'); target.focus({preventScroll:true}); } }, options);
      const previewButton = this.host?.querySelector('[data-xgg-open]');
      previewButton?.addEventListener('click', () => this.openGame(previewButton), options);
      const nativeButton = this.host?.querySelector('.xb-free-feature [data-x-new-card] [data-x-choose]');
      if (this.artist === 'bunny' && nativeButton) nativeButton.addEventListener('click', event => {
        if (this.forwarding) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openGame(nativeButton);
      }, {capture: true, signal: this.controller.signal});
      this.q('[data-xgg-catch]').addEventListener('click', () => this.catchBox(), options);
      this.q('[data-xgg-replay]').addEventListener('click', () => this.catchBox(), options);
      this.q('[data-xgg-purchase]').addEventListener('click', () => this.requestGift(), options);
      this.q('[data-xgg-skip]').addEventListener('click', () => {
        this.closeGame();
      }, options);
      document.addEventListener('visibilitychange', () => this.tick(), options);
      window.addEventListener('storage', event => {
        if (event.key === this.storageKey()) { this.readRound(); this.tick(); }
      }, options);
      this.langObserver = new MutationObserver(() => this.syncLanguage());
      this.langObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['lang']});
      this.syncLanguage();
      this.tick();
      this.timer = setInterval(() => this.tick(), 250);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.langObserver?.disconnect();
      clearInterval(this.timer);
      this.ready = false;
    }
    storageKey() { return `xdrop.visual-round.v1.${this.artist}`; }
    readRound() {
      try {
        const value = Number(localStorage.getItem(this.storageKey()));
        this.end = Number.isFinite(value) && value > 0 && value <= Date.now() + DURATION ? value : 0;
      } catch { this.end = 0; }
    }
    syncLanguage() {
      const next = (document.documentElement.lang || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
      if (next === this.lang) return;
      this.lang = next;
      this.setAttribute('lang', next);
      this.querySelectorAll('[data-xgg-en]').forEach(node => {
        node.textContent = node.dataset[next === 'es' ? 'xggEs' : 'xggEn'];
      });
      this.host?.querySelectorAll('[data-xgg-badge]').forEach(node => {
        node.textContent = node.dataset[next === 'es' ? 'xggEs' : 'xggEn'];
        node.setAttribute('lang', next);
      });
      this.dialog.setAttribute('aria-label', next==='es'?'Juego de caja misteriosa':'Mystery box game');
      this.q('[data-xgg-close]').setAttribute('aria-label', next==='es'?'Cerrar juego':'Close game');
      this.q('[data-xgg-catch]').setAttribute('aria-label', COPY[next].catch);
      this.q('[data-xgg-clock]').setAttribute('aria-label', COPY[next].remaining);
      this.paint();
    }
    status(key, literal = '') {
      this.messageKey = key;
      this.message = literal;
      this.q('[data-xgg-status]').textContent = literal || COPY[this.lang][key] || '';
    }
    startRound() {
      if (this.end > Date.now()) return;
      this.end = Date.now() + DURATION;
      this.caught = false;
      try { localStorage.setItem(this.storageKey(), String(this.end)); } catch {}
      this.paint();
    }
    openGame(opener) {
      this.opener = opener || document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      if (!this.end) this.startRound();
      this.paint();
      this.dialog.scrollTop = 0;
      this.q('[data-xgg-close]').focus({preventScroll:true});
    }
    closeGame() { if (this.dialog.open) this.dialog.close(); }
    catchBox() {
      if (this.end > Date.now() && this.caught) return;
      if (this.end <= Date.now()) this.startRound();
      this.caught = true;
      this.status('success');
      this.paint();
      this.confetti();
    }
    confetti() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const tray = this.q('[data-xgg-confetti]');
      for (let i = 0; i < 18; i += 1) {
        const piece = document.createElement('i');
        piece.style.setProperty('--xgg-x', `${(Math.random() - 0.5) * 260}px`);
        piece.style.setProperty('--xgg-y', `${(Math.random() - 0.75) * 210}px`);
        piece.style.setProperty('--xgg-r', `${Math.random() * 600}deg`);
        tray.append(piece);
        setTimeout(() => piece.remove(), 1000);
      }
    }
    nativeState() {
      if (this.artist !== 'bunny' || !this.host?.matches('x-drop-halloween')) return {};
      const card = this.host.querySelector('.xb-free-feature [data-x-new-card]');
      const button = card?.querySelector('[data-x-choose]');
      const available = this.host.ready === true && typeof this.host.add === 'function' &&
        !!card?.dataset.variant && card.dataset.available === 'true' && !!button && !button.disabled;
      return {card, button, available};
    }
    requestGift() {
      // The visual round is intentionally not checked here: playing is never a gate.
      if (this.artist === 'karol') { this.requestKarol(); return; }
      if (this.pending || this.completed || this.host?.pending) return;
      const native = this.nativeState();
      if (!native.available) { this.status('unavailable'); this.paintPurchase(); return; }
      this.pending = true;
      this.status('adding');
      this.paintPurchase();
      try {
        this.forwarding = true;
        try { native.button.click(); } finally { this.forwarding = false; }
        // Existing handler starts synchronously and owns all validation and cart mutations.
        if (!this.host.pending) {
          this.pending = false;
          this.status('fallback');
        }
      } catch {
        this.pending = false;
        this.status('error');
      }
      this.paintPurchase();
    }
    async requestKarol() {
      if (this.pending || this.completed || this.dataset.karolReady !== 'true' || !window.XDropGiftLimit) return;
      this.pending=true; this.status('adding'); this.paintPurchase();
      try {
        const root=window.Shopify?.routes?.root||'/';
        const productResponse=await fetch(root+'products/caja-sorpresa-karol-g-gratis.js',{headers:{Accept:'application/json'},cache:'no-store'});
        if(!productResponse.ok) throw Error(COPY[this.lang].unavailable);
        const product=await productResponse.json();
        const variant=product.variants?.find(v=>String(v.id)==='50859956502576');
        if(!product.available || !product.published_at || !variant?.available || Number(variant.price)!==0) throw Error(COPY[this.lang].unavailable);
        const response=await fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1,properties:{Collection:'Karol G'}}]})});
        const data=await response.json();
        if(!response.ok) throw Error(data.description||data.message||COPY[this.lang].error);
        this.completed=true; this.status('added');
        const cartResponse=await fetch(root+'cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
        if(!cartResponse.ok) throw Error(COPY[this.lang].error);
        const cart=await cartResponse.json();
        document.querySelectorAll('[data-kg-count],.kg-bag span').forEach(e=>e.textContent=cart.item_count);
        this.completed=true; this.status('added');
      } catch(error) { if(!this.completed) this.status('error',error.message||COPY[this.lang].error); }
      finally { this.pending=false; this.paintPurchase(); }
    }
    tick() {
      if (this.artist === 'bunny' && this.pending && !this.host?.pending) {
        this.pending = false;
        this.completed = this.host?.statusKey === 'added';
        const nativeMessage = this.host?.querySelector('[data-x-status]')?.textContent || '';
        this.status(this.completed ? 'added' : 'error', nativeMessage);
      }
      this.paint();
    }
    paintPurchase() {
      const button = this.q('[data-xgg-purchase]');
      if (!button) return;
      const busy = this.pending || !!this.host?.pending;
      const available = this.artist==='karol' ? this.dataset.karolReady==='true' && !!window.XDropGiftLimit : this.nativeState().available;
      button.disabled = busy || this.completed || !available;
      button.setAttribute('aria-busy', String(busy));
      const key = busy ? 'adding' : this.completed ? 'added' : available ? 'add' : this.artist==='karol' ? 'soon' : 'unavailable';
      const copy = COPY[this.lang || 'en'][key];
      if (button.textContent !== copy) button.textContent = copy;
    }
    paint() {
      if (!this.lang) return;
      const seconds = this.end ? Math.max(0, Math.ceil((this.end - Date.now()) / 1000)) : 900;
      const active = seconds > 0;
      const expired = !!this.end && !active;
      this.dataset.state = active && this.caught ? 'caught' : expired ? 'expired' : 'idle';
      this.q('[data-xgg-round]').hidden = false;
      const clock = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      if (this.q('[data-xgg-clock]').textContent !== clock) this.q('[data-xgg-clock]').textContent = clock;
      this.q('[data-xgg-progress]').style.width = `${Math.min(100, seconds / 900 * 100)}%`;
      this.q('[data-xgg-replay]').hidden = !expired;
      this.q('[data-xgg-catch]').setAttribute('aria-disabled', String(active && this.caught));
      this.q('[data-xgg-tap]').textContent = COPY[this.lang][active && this.caught ? 'caught' : 'tap'];
      if (expired && (!this.messageKey || this.messageKey === 'success')) this.messageKey = 'expired';
      const message = this.message || COPY[this.lang][this.messageKey] || '';
      if (this.q('[data-xgg-status]').textContent !== message) this.q('[data-xgg-status]').textContent = message;
      this.paintPurchase();
    }
  }
  customElements.define('xdrop-gift-game', GiftGame);
})();
